import { useRef, useState } from "react";
import { GripVertical } from "lucide-react";

/**
 * Dependency-free HTML5 drag-and-drop list.
 *
 * Props:
 *   items       — ordered array
 *   getId       — function(item) => stable id used as key
 *   renderRow   — function(item, dragHandle) => JSX; `dragHandle` is the icon you should place inside the row
 *   onReorder   — function(newlyOrderedItems, newIdList) => Promise|void. Called after a drop that changed order.
 *   testIdPrefix — optional data-testid prefix per row
 */
export default function DragList({ items, getId, renderRow, onReorder, testIdPrefix = "drag-row" }) {
  const [dragging, setDragging] = useState(null);   // id being dragged
  const [over, setOver] = useState(null);           // id currently hovered
  const orderRef = useRef(items);
  orderRef.current = items;

  const handleDragStart = (id) => (e) => {
    setDragging(id);
    e.dataTransfer.effectAllowed = "move";
    // Firefox requires setData for the drag to actually start
    try { e.dataTransfer.setData("text/plain", id); } catch { /* ignore */ }
  };

  const handleDragOver = (id) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (over !== id) setOver(id);
  };

  const handleDragLeave = () => setOver(null);

  const handleDrop = (targetId) => (e) => {
    e.preventDefault();
    const sourceId = dragging;
    setDragging(null);
    setOver(null);
    if (!sourceId || sourceId === targetId) return;
    const arr = [...orderRef.current];
    const sIdx = arr.findIndex((it) => getId(it) === sourceId);
    const tIdx = arr.findIndex((it) => getId(it) === targetId);
    if (sIdx < 0 || tIdx < 0) return;
    const [moved] = arr.splice(sIdx, 1);
    arr.splice(tIdx, 0, moved);
    onReorder?.(arr, arr.map(getId));
  };

  const handleDragEnd = () => { setDragging(null); setOver(null); };

  return (
    <ul className="divide-y">
      {items.map((it, i) => {
        const id = getId(it);
        const isDragging = dragging === id;
        const isOver = over === id && dragging !== id;
        const dragHandle = (
          <button
            type="button"
            aria-label="Drag to reorder"
            className="mr-1 -ml-1 inline-grid h-8 w-8 cursor-grab place-items-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 active:cursor-grabbing"
            data-testid={`${testIdPrefix}-${id}-handle`}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        );
        return (
          <li
            key={id}
            draggable
            onDragStart={handleDragStart(id)}
            onDragOver={handleDragOver(id)}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop(id)}
            onDragEnd={handleDragEnd}
            data-testid={`${testIdPrefix}-${id}`}
            data-drag-index={i}
            className={[
              "transition-colors",
              isDragging ? "opacity-40" : "",
              isOver ? "bg-primary/5 ring-1 ring-inset ring-primary/40" : "hover:bg-slate-50",
            ].join(" ")}
          >
            {renderRow(it, dragHandle)}
          </li>
        );
      })}
    </ul>
  );
}
